import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetchBatchResolver } from 'reference-resolvers-lite/ResolverHooks';
import { parseMultiEnumValue } from 'customer-data-property-utils/parseMultiEnumValue';
import { isLoading, isError } from 'reference-resolvers-lite/Status';
import { Button } from 'foundations-components/transitional/components/Button';
import { Popover, PopoverBody, PopoverFooter } from 'foundations-components/transitional/components/Popover';
import { View } from 'foundations-components/transitional/components/View';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { Flex } from 'foundations-components/components/Flex';
import { useSendCrmMessageTopic } from 'crm-message-bus/useSendCrmMessageTopic';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { createStandardCachedResolver } from 'reference-resolvers-lite/utils/createStandardCachedResolver';
import { TASK_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { useIsCrmCommandHandlerAvailable } from '../../v2/utils/useIsCrmCommandHandlerAvailable';
import { EMPTY_PROPERTY_VALUE } from '../../constants/EmptyPropertyValue';
import { indexBy } from '../../utils/indexBy';
import { SkeletonText } from '../../loaders/SkeletonText';
import HubSpotAppLink from 'crm-links/common/components/HubSpotAppLink';
import { Paginator } from 'foundations-components/transitional/components/Paginator';
import PortalIdParser from 'PortalIdParser';
import { useOnCrmMessageTopic } from 'crm-message-bus/useOnCrmMessageTopic';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// Note: We use 5 sub-task to show to prevent the popover from being too big
const PAGE_SIZE = 5;
const indexById = indexBy(({
  id
}) => id);
function splitArrayByPage(array, pageSize) {
  return array.reduce((acc, subtask, index) => {
    if (index % pageSize === 0) {
      acc.push([]);
    }
    acc[acc.length - 1].push(subtask);
    return acc;
  }, []);
}
const shouldRefetchReference = (objectId, objectTypeId, objectPropertyNames, referenceIdMap) => {
  return objectTypeId === TASK_TYPE_ID && referenceIdMap[objectId] && objectPropertyNames.includes('hs_task_subject');
};
const ResolvedSubtasksCell = ({
  objectId,
  objectTypeId,
  value
}) => {
  const [page, setPage] = useState(1);
  const [open, setPopoverOpen] = useState(false);
  const [referenceIdMap, setReferenceIdMap] = useState({});
  const timeoutRef = useRef(null);
  const resolver = createStandardCachedResolver(TASK_TYPE_ID, objectTypeId);
  const isCrmCommandHandlerAvailable = useIsCrmCommandHandlerAvailable();
  const sendPreviewEngagementMessage = useSendCrmMessageTopic(TOPIC_NAMES.PREVIEW_ENGAGEMENT);
  const [rawSubtaskIds, setRawSubtaskIds] = useState(parseMultiEnumValue(value));
  const {
    references,
    status
  } = useFetchBatchResolver(resolver, rawSubtaskIds);
  const needsPagination = rawSubtaskIds.length > PAGE_SIZE;
  const truePageValue = page - 1;
  const numberOfSubTasks = rawSubtaskIds.length;
  useEffect(() => {
    setRawSubtaskIds(parseMultiEnumValue(value));
  }, [setRawSubtaskIds, value]);
  const openSubtaskOrParentTask = useCallback(({
    engagementId
  }) => {
    if (isCrmCommandHandlerAvailable) {
      sendPreviewEngagementMessage({
        engagementId
      });
    }
  }, [isCrmCommandHandlerAvailable, sendPreviewEngagementMessage]);
  const openPopover = useCallback(() => {
    setPopoverOpen(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);
  const closePopover = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setPopoverOpen(false);
    }, 300);
  }, []);
  const onViewAllSubtasksClick = useCallback(() => {
    if (isCrmCommandHandlerAvailable) {
      openSubtaskOrParentTask({
        engagementId: String(objectId)
      });
    }
  }, [isCrmCommandHandlerAvailable, objectId, openSubtaskOrParentTask]);
  const onClickIndividualSubtask = useCallback(engagementId => {
    if (isCrmCommandHandlerAvailable) {
      sendPreviewEngagementMessage({
        engagementId
      });
    }
  }, [isCrmCommandHandlerAvailable, sendPreviewEngagementMessage]);
  const onHandleObjectUpdates = useCallback(updatedObjectIds => {
    if (!references) {
      return;
    }
    updatedObjectIds.forEach(updatedObjectData => {
      const updatedObjectId = updatedObjectData.objectId;
      const updatedObjectTypeId = updatedObjectData.objectTypeId;
      const updatedObjectPropertyNames = updatedObjectData.propertyNames || [];
      if (shouldRefetchReference(updatedObjectId, updatedObjectTypeId, updatedObjectPropertyNames, referenceIdMap)) {
        resolver.api.refreshCache(updatedObjectId).then(result => {
          setReferenceIdMap(prev => Object.assign({}, prev, {
            [updatedObjectId]: result
          }));
        }).catch(() => {
          return;
        });
      }
    });
  }, [references, referenceIdMap, resolver.api]);
  const onHandleObjectsDeleted = useCallback(deletedObjectData => {
    const containsDeletedSubtasks = deletedObjectData.some(data => referenceIdMap[data.objectId] && data.objectTypeId === TASK_TYPE_ID);
    if (containsDeletedSubtasks) {
      setRawSubtaskIds(prev => prev.filter(id => !deletedObjectData.some(data => data.objectId === id)));
    }
  }, [referenceIdMap]);
  useOnCrmMessageTopic(TOPIC_NAMES.OBJECTS_DELETED, onHandleObjectsDeleted);
  useOnCrmMessageTopic(TOPIC_NAMES.UPDATE_OBJECTS, onHandleObjectUpdates);
  useEffect(() => {
    if (references) {
      setReferenceIdMap(indexById(references));
    }
  }, [references]);
  const subtasks = useMemo(() => rawSubtaskIds.map(id => {
    const idStr = String(id);
    const reference = Object.prototype.hasOwnProperty.call(referenceIdMap, idStr) ? referenceIdMap[idStr] : undefined;
    if (!reference) {
      return {
        id,
        label: `${id}`
      };
    }
    if (!reference.label) {
      return {
        id,
        label: `${reference.id}`
      };
    }
    return reference;
  }), [rawSubtaskIds, referenceIdMap]);
  const pagedSubtasks = useMemo(() => {
    return splitArrayByPage(subtasks, PAGE_SIZE);
  }, [subtasks]);
  if (isError(status)) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: rawSubtaskIds.map(id => {
        return /*#__PURE__*/_jsx(Text, {
          children: id
        }, id);
      })
    });
  }
  if (isLoading(status)) {
    return /*#__PURE__*/_jsx(SkeletonText, {
      "data-test-id": "loading-text"
    });
  }
  if (!references || !objectId || rawSubtaskIds.length === 0) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: EMPTY_PROPERTY_VALUE
    });
  }
  if (subtasks.length === 1) {
    if (isCrmCommandHandlerAvailable) {
      return /*#__PURE__*/_jsx(Button, {
        use: "link",
        onClick: () => openSubtaskOrParentTask({
          engagementId: String(subtasks[0].id)
        }),
        children: subtasks[0].label
      });
    }
    return /*#__PURE__*/_jsx(Text, {
      children: subtasks[0].label
    });
  }
  const ViewAllSubtasksButton = isCrmCommandHandlerAvailable ? /*#__PURE__*/_jsx(Button, {
    use: "link",
    onClick: onViewAllSubtasksClick,
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "table.subtasksCell.viewSubtasks"
    })
  }) : /*#__PURE__*/_jsx(HubSpotAppLink, {
    linkType: "PAGE",
    linkTypeProps: {
      path: `/tasks/${PortalIdParser.get()}/view/all`
    },
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "table.subtasksCell.viewSubtasks"
    })
  });
  return /*#__PURE__*/_jsx(Popover, {
    open: open,
    placement: "bottom",
    onOpenChange: e => setPopoverOpen(e.currentTarget.value),
    content: /*#__PURE__*/_jsxs(View, {
      inlineSize: "223px",
      onMouseEnter: () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      },
      onMouseLeave: closePopover,
      children: [/*#__PURE__*/_jsx(PopoverBody, {
        paddingBlockStart: "300",
        paddingInline: "500",
        paddingBlockEnd: isCrmCommandHandlerAvailable ? '0' : '300',
        children: /*#__PURE__*/_jsx(List, {
          children: pagedSubtasks[truePageValue].map(subtask => {
            return /*#__PURE__*/_jsx(ListItem, {
              children: isCrmCommandHandlerAvailable ? /*#__PURE__*/_jsx(Button, {
                use: "link",
                onClick: () => onClickIndividualSubtask(String(subtask.id)),
                children: subtask.label
              }) : /*#__PURE__*/_jsx(Text, {
                children: subtask.label
              })
            }, subtask.label);
          })
        })
      }), /*#__PURE__*/_jsx(PopoverFooter, {
        paddingBlockStart: "400",
        paddingBlockEnd: "300",
        paddingInline: "500",
        children: needsPagination ? /*#__PURE__*/_jsxs(Flex, {
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: "200",
          children: [/*#__PURE__*/_jsx(Paginator, {
            maxVisiblePageButtons: 0,
            pageCount: pagedSubtasks.length,
            page: page,
            onPageChange: e => setPage(e.target.value)
          }), ViewAllSubtasksButton]
        }) : /*#__PURE__*/_jsx(Flex, {
          justifyContent: "end",
          children: ViewAllSubtasksButton
        })
      })]
    }),
    children: /*#__PURE__*/_jsx(Button, {
      padding: "0",
      use: "transparent",
      onMouseEnter: openPopover,
      onMouseLeave: closePopover,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "table.subtasksCell.numberOfRecords",
        options: {
          count: numberOfSubTasks
        }
      })
    })
  });
};
export default ResolvedSubtasksCell;